/******************************************
 * AUTHOR:          Ala.Co
 * DESCRIPTION:     Super-Secure Axios Client
 * LEVEL:           Ultra-Stable + Multi-Tab + CSRF + XSS-Safe
 ******************************************/

import axios from "axios";
import ConfigViweService from "../utils/ConfigViewService";
import {
  getAccessToken,
  getRefreshToken,
  getTokenTime,
  setTokens,
  removeTokens,
  isTokenExpired,
  ensureCsrfToken,
  getCsrfToken,
} from "./auth";
import { errorMessage } from "../utils/alert";

const { showLoading, hideLoading } = ConfigViweService();

/* ---------------- Base API ---------------- */
const BaseUrl = "https://emrapi.shafa33.ir/api/v1/";
//const BaseUrl = "https://localhost:7261/api/v1/";
export const mainServer = axios.create({ baseURL: BaseUrl });

/* ---------------- Global State ---------------- */
let isRefreshing = false;
let refreshPromise = null;
let failedQueue = [];

// برای Multi-Tab Sync
const AUTH_SYNC_KEY = "shafa33_auth_sync";
const REFRESH_LOCK_KEY = "shafa33_refresh_lock";
const isBrowser = typeof window !== "undefined";
const TAB_ID = isBrowser
  ? `${Date.now()}_${Math.random().toString(16).slice(2)}`
  : "server-tab";

let authChannel = null;
if (isBrowser && "BroadcastChannel" in window) {
  authChannel = new BroadcastChannel("shafa33-auth-channel");
}

/* ---------------- Helper: XSS-safe sanitize ---------------- */
const sanitizeText = (txt, maxLen = 300) => {
  if (!txt) return "";
  let s = String(txt);
  if (s.length > maxLen) s = s.slice(0, maxLen) + "…";
  // حذف کاراکترهای خطرناک HTML
  s = s.replace(/[<>]/g, "");
  return s;
};

const logApiError = (info) => {
  const safeInfo = {
    api: sanitizeText(info?.api, 200),
    method: info?.method,
    errorCode: info?.errorCode,
    error: sanitizeText(info?.error),
    time: info?.time,
    retry: !!info?.retry,
  };
  // فقط لاگ امن
  console.error("[API-ERROR]", safeInfo);
};

/* ---------------- Queue Processor ---------------- */
const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve(token);
  });
  failedQueue = [];
};

/* ---------------- Multi-Tab Event Sync ---------------- */
const handleAuthEvent = (event) => {
  if (!event) return;

  switch (event.type) {
    case "TOKEN_REFRESHED": {
      // توکن جدید از Cookie خوانده می‌شود (نه از storage)
      const latestToken = getAccessToken();

      if (latestToken) {
        mainServer.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${latestToken}`;
        // تمام ریکوئست‌هایی که منتظر Refresh بودند آزاد شوند
        processQueue(null, latestToken);
      } else {
        // اگر به هر دلیل توکن در کوکی موجود نیست → لاگ‌اوت سراسری
        processQueue(new Error("No token after refresh"), null);
        forceLogout();
      }
      break;
    }

    case "LOGOUT":
      // اینجا Event از تب دیگر آمده است
      removeTokens();
      if (isBrowser) {
        window.location.href = "/auth/login";
      }
      break;

    default:
      break;
  }
};

const broadcastAuthEvent = (payload) => {
  if (!isBrowser) return;

  // BroadcastChannel (در حافظه، بین تب‌ها)
  if (authChannel) {
    authChannel.postMessage(payload);
  }

  // localStorage fallback برای sync بین تب‌ها
  try {
    const minimal = {
      type: payload.type,
      ts: Date.now(),
      tabId: TAB_ID,
    };
    sessionStorage.setItem(AUTH_SYNC_KEY, JSON.stringify(minimal));
  } catch {
    // ignore
  }
};

if (isBrowser) {
  if (authChannel) {
    authChannel.onmessage = (e) => handleAuthEvent(e.data);
  }

  window.addEventListener("storage", (e) => {
    // توجه: storage event فقط برای localStorage بین تب‌ها کار می‌کند
    if (e.key === AUTH_SYNC_KEY && e.newValue) {
      try {
        const data = JSON.parse(e.newValue);
        handleAuthEvent(data);
      } catch {
        // ignore
      }
    }
  });
}

/* ---------------- Multi-Tab Refresh Lock ---------------- */
const acquireRefreshLock = () => {
  if (!isBrowser) return true;

  const now = Date.now();
  const TTL = 30_000; // 30 ثانیه

  try {
    const raw = localStorage.getItem(REFRESH_LOCK_KEY);
    if (raw) {
      const lock = JSON.parse(raw);
      if (lock && now - lock.ts < TTL) {
        // قفل هنوز معتبر است => تب دیگری Refresh انجام می‌دهد
        if (lock.tabId !== TAB_ID) return false;
      }
    }

    const newLock = { ts: now, tabId: TAB_ID };
    localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(newLock));
    return true;
  } catch {
    return true;
  }
};

const releaseRefreshLock = () => {
  if (!isBrowser) return;

  try {
    const raw = localStorage.getItem(REFRESH_LOCK_KEY);
    if (!raw) return;

    const lock = JSON.parse(raw);
    if (lock && lock.tabId === TAB_ID) {
      localStorage.removeItem(REFRESH_LOCK_KEY);
    }
  } catch {
    // ignore
  }
};

/* ---------------- Force Logout (multi-tab) ---------------- */
const forceLogout = () => {
  // این تب
  removeTokens();
  // به همه تب‌ها اعلام کن
  broadcastAuthEvent({ type: "LOGOUT" });
  errorMessage("نشست شما به پایان رسیده است. لطفاً دوباره وارد شوید.");
  if (isBrowser) {
    window.location.href = "/auth/login";
  }
};

/* ---------------- CSRF Helper ---------------- */
const METHODS_NEED_CSRF = ["post", "put", "patch", "delete"];

const attachCsrfHeader = (config) => {
  const method = (config.method || "get").toLowerCase();
  if (!METHODS_NEED_CSRF.includes(method)) return config;

  const csrfToken = ensureCsrfToken() || getCsrfToken();
  if (csrfToken) {
    config.headers = config.headers || {};
    config.headers["X-CSRF-Token"] = csrfToken;
  }

  return config;
};

/* ---------------- Refresh Token ---------------- */
const refreshToken = async () => {
  const refreshValue = getRefreshToken();
  if (!refreshValue) {
    forceLogout();
    throw new Error("No refresh token");
  }

  try {
    const res = await axios.post(`${BaseUrl}Users/RefreshToken`, {
      refreshToken: refreshValue,
    });

    const { token, refreshToken: newRefreshToken, dateTimeNow } = res.data.data;

    setTokens(token, newRefreshToken, dateTimeNow);

    // اطلاع به تمام تب‌ها (اطلاعات واقعی توکن از Cookie خوانده می‌شود)
    broadcastAuthEvent({
      type: "TOKEN_REFRESHED",
    });

    return token;
  } catch (err) {
    logApiError({
      api: "Users/RefreshToken",
      method: "POST",
      error: err?.message,
      errorCode: err?.response?.status || null,
      retry: false,
      time: new Date().toISOString(),
    });

    forceLogout();
    throw err;
  }
};

/* ---------------- Request Interceptor ---------------- */
mainServer.interceptors.request.use(
  async (config) => {
    // اگر لازم است، CSRF را اضافه کن
    config = attachCsrfHeader(config);

    const token = getAccessToken();
    if (!token) {
      // بدون توکن → ریکوئست بدون Authorization می‌رود (ممکن است public API باشد)
      return config;
    }

    // اگر هنوز منقضی نشده
    if (!isTokenExpired(token)) {
      config.headers = config.headers || {};
      config.headers["Authorization"] = `Bearer ${token}`;
      return config;
    }

    // منقضی شده → مسئله Multi-Tab
    const hasLock = acquireRefreshLock();

    if (hasLock) {
      // این تب مسئول Refresh است
      if (!isRefreshing) {
        isRefreshing = true;

        refreshPromise = refreshToken()
          .then((newToken) => {
            processQueue(null, newToken);
            return newToken;
          })
          .catch((err) => {
            processQueue(err, null);
            throw err;
          })
          .finally(() => {
            isRefreshing = false;
            refreshPromise = null;
            releaseRefreshLock();
          });
      }

      const newToken = await refreshPromise;
      if (!newToken) {
        // اگر Refresh موفق نبود → لاگ‌اوت سراسری
        forceLogout();
        throw new Error("Unable to refresh token");
      }

      config.headers = config.headers || {};
      config.headers["Authorization"] = `Bearer ${newToken}`;
      return config;
    }

    // تب دیگر Refresh انجام می‌دهد → منتظر می‌مانیم
    return new Promise((resolve, reject) => {
      failedQueue.push({
        resolve: (newToken) => {
          if (!newToken) {
            forceLogout();
            return reject(new Error("Unable to refresh token from other tab"));
          }
          config.headers = config.headers || {};
          config.headers["Authorization"] = `Bearer ${newToken}`;
          resolve(config);
        },
        reject,
      });
    });
  },
  (error) => Promise.reject(error)
);

/* ---------------- Response Interceptor ---------------- */
mainServer.interceptors.response.use(
  (response) => {
    // اگر API در پاسخ توکن جدید داد
    if (response?.data?.data?.token) {
      const { token, refreshToken, dateTimeNow } = response.data.data;
      setTokens(token, refreshToken, dateTimeNow);

      // Sync بین تب‌ها (اطلاعات واقعی از Cookie خوانده می‌شود)
      broadcastAuthEvent({
        type: "TOKEN_REFRESHED",
      });
    }

    return response;
  },
  async (error) => {
    const req = error.config;

    // اگر 401
    if (error.response?.status === 401 && req) {
      // حالت 1: یک‌بار تلاش برای Refresh (اگر ممکن باشد)
      if (!req._retry && !isRefreshing && getRefreshToken()) {
        req._retry = true;

        try {
          const newToken = await refreshToken();
          if (!newToken) {
            forceLogout();
            return Promise.reject(error);
          }
          req.headers = req.headers || {};
          req.headers["Authorization"] = `Bearer ${newToken}`;
          return mainServer(req);
        } catch (err) {
          // Refresh شکست خورد
          forceLogout();
          return Promise.reject(err);
        }
      }

      // حالت 2: یا قبلاً Retry شده یا RefreshToken نداریم → لاگ‌اوت سراسری
      forceLogout();
    }

    // پیام خطا را XSS-safe نمایش بده
    const serverMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message;
    logApiError({
      api: req?.url,
      method: req?.method,
      error: serverMessage,
      errorCode: error.response?.status,
      retry: !!req?._retry,
      time: new Date().toISOString(),
    });

    return Promise.reject(error);
  }
);
